import QUESTION_TYPE, { OPT_IN } from '@v4/constants/questions'
import * as Yup from 'yup'
import { getWording } from '@v4/utils/wordings'
import { getUserLocale, getUserTimeZone } from '@v4/utils/general'

export const formatValues = (
	values = {},
	initialValues = {
		user_tz: getUserTimeZone(),
		user_locale: getUserLocale(),
	},
) => {
	return _.reduce(
		values,
		(acc, value, key) => {
			if (import.meta.env.VITE_HONEY_POT_FIELD && key === import.meta.env.VITE_HONEY_POT_FIELD) {
				acc[key] = value || undefined
			} else if (typeof value === 'undefined' || value === null || value === '' || value === false) {
				acc[key] = null
			} else if (typeof value === 'object') {
				const parsedValue =
					key === 'questions' && !_.isEmpty(value)
						? _.reduce(
								value,
								(questionAcc, questionValue, questionKey) => {
									// eslint-disable-next-line no-param-reassign
									questionAcc[`${questionKey}`.replace('key_', '')] = questionValue
									return questionAcc
								},
								{},
						  )
						: value
				acc[key] = formatValues(parsedValue, {})
			} else {
				acc[key] = value
			}
			return acc
		},
		initialValues,
	)
}

export const formatErrors = errors => {
	if (errors instanceof Error) {
		return {
			_error: errors.message,
		}
	}

	return _.reduce(
		errors,
		(acc, value, key) => {
			if (typeof value === 'object') {
				const parsedValue =
					key === 'questions' && !_.isEmpty(value)
						? _.reduce(
								value,
								(questionAcc, questionValue, questionKey) => {
									// eslint-disable-next-line no-param-reassign
									questionAcc[`key_${questionKey}`] = questionValue
									return questionAcc
								},
								{},
						  )
						: value
				acc[key] = formatErrors(parsedValue)
			} else {
				acc[key] = value
			}
			return acc
		},
		{},
	)
}

export const formatAnalyticData = (values, data) => {
	const entities = _.reduce(
		data,
		(acc, questions) => {
			_.forEach(questions, question => {
				acc[question.id] = question
			})

			return acc
		},
		{},
	)
	return _.reduce(
		values,
		(acc, value, key) => {
			if (import.meta.env.VITE_HONEY_POT_FIELD && key === import.meta.env.VITE_HONEY_POT_FIELD) {
				return acc
			}
			if (key !== 'questions') {
				acc[key] = value

				return acc
			}

			acc[key] = _.reduce(
				value,
				(questionsAcc, answer, questionId) => {
					const question = entities[questionId]
					if (!question || answer === null) {
						return questionsAcc
					}

					if (
						[QUESTION_TYPE.DROPDOWN, QUESTION_TYPE.INDUSTRY, QUESTION_TYPE.COUNTRY].includes(
							question.answer_type,
						)
					) {
						const option = answer && _.find(question.options, { id: answer })

						// eslint-disable-next-line no-param-reassign
						questionsAcc[questionId] = option?.option
					} else {
						// eslint-disable-next-line no-param-reassign
						questionsAcc[questionId] = answer
					}

					return questionsAcc
				},
				{},
			)

			return acc
		},
		{},
	)
}

export const getSignUpFormikProps = (items = {}, settings = {}, query = {}) => {
	const dynamicFields = [
		...items[QUESTION_TYPE.OTHER],
		...items[QUESTION_TYPE.CHECKBOX],
		...items[QUESTION_TYPE.HIDDEN],
	]

	const requiredMessage = () => getWording('validationRequired')
	const emailMessage = () => getWording('validationEmail')
	const dropdownMessage = () => getWording('validationDropdown')
	const phoneMessage = () => getWording('validationPhone')
	const requiredCheckboxMessage = () => getWording('validationRequiredCheckbox')
	const optInMessage = () => getWording('validationOptIn')
	const maxLengthMessage = () => getWording('validationMaxLength', { max: 255, count: 255 })

	const initialValues = {}
	const prefillValues = {}
	const shape = {}
	if (settings.email_enabled) {
		shape.email = Yup.string()
			.max(255, maxLengthMessage)
			.email(emailMessage)
			.required(requiredMessage)
			.label(getWording(settings.email_label || 'Email', { ns: 'lead' }))
		const initialEmail = decodeURIComponent(query.email || '')
		initialValues.email = initialEmail

		if (!_.isEmpty(initialEmail)) {
			prefillValues.email = initialEmail
		}
	}

	if (settings.firstname_enabled) {
		shape.first_name = Yup.string()
			.max(255, maxLengthMessage)
			.required(requiredMessage)
			.label(getWording(settings.firstname_label || 'First Name', { ns: 'lead' }))
		const initialFirstName = decodeURIComponent(query.first_name || '')
		initialValues.first_name = initialFirstName

		if (!_.isEmpty(initialFirstName)) {
			prefillValues.first_name = initialFirstName
		}
	}

	const questionShape = {}
	if (settings.lastname_enabled) {
		shape.last_name = Yup.string()
			.max(255, maxLengthMessage)
			.required(requiredMessage)
			.label(getWording(settings.lastname_label || 'Last Name', { ns: 'lead' }))
		const initialLastName = decodeURIComponent(query.last_name || '')
		initialValues.last_name = initialLastName

		if (!_.isEmpty(initialLastName)) {
			prefillValues.last_name = initialLastName
		}
	}

	if (dynamicFields.length) {
		initialValues.questions = {}
		prefillValues.questions = {}
	}
	dynamicFields.forEach(item => {
		const key = `key_${item.id}`
		let validation
		let initial = decodeURIComponent(query[item.id] || query[item.slug] || '')
		switch (item.answer_type) {
			case QUESTION_TYPE.CHECKBOX: {
				validation = Yup.bool()
				initial = Boolean(initial) || false
				break
			}
			case QUESTION_TYPE.DROPDOWN:
			case QUESTION_TYPE.INDUSTRY:
			case QUESTION_TYPE.COUNTRY: {
				initial = _.find(item.options, ({ option }) => option === initial)?.id || null
				validation = Yup.mixed().oneOf([null, ...item.options.map(({ id }) => id)], dropdownMessage)
				const { default_country: defaultCountry } = item.settings || {}
				if (item.answer_type === QUESTION_TYPE.COUNTRY && defaultCountry && !initial) {
					const defaultOption = item.options.find(({ option }) => option === defaultCountry)
					initial = defaultOption ? defaultOption.id : null
				}
				break
			}
			case QUESTION_TYPE.PHONE: {
				validation = Yup.string().phone(null, false, phoneMessage)
				break
			}
			default:
				validation = Yup.string().max(1000)

				break
		}

		validation.label(item.question)

		if (item.required) {
			if (item.answer_type === QUESTION_TYPE.CHECKBOX) {
				validation = validation.oneOf([true], requiredCheckboxMessage)
			}
			validation = validation.required(requiredMessage)
		}

		questionShape[key] = validation
		initialValues.questions[key] = initial

		if (!_.isEmpty(initial)) {
			prefillValues.questions[item.id] = initial
		}
	})

	if (_.isEmpty(prefillValues.questions)) {
		delete prefillValues.questions
	}

	if (dynamicFields.length) {
		shape.questions = Yup.object().shape(questionShape)
	}
	if (settings.optin === OPT_IN.EXPLICIT_CONSENT_REQUIRED) {
		shape.opt_in = Yup.bool()
			.oneOf([true], optInMessage)
			.required(requiredMessage)
	}
	if (settings.optin !== OPT_IN.IMPLIED_CONSENT) {
		initialValues.opt_in = false
	}

	if (query.opt_in) {
		initialValues.opt_in = query.opt_in !== OPT_IN.OPTIN_NONE
	}

	return { formikProps: { validationSchema: Yup.object().shape(shape), initialValues }, prefillValues }
}

export const getAnswersFromQuery = search => {
	const queryString = search ? search.slice(1) : ''
	const params = {}

	queryString.replace(/([^=]*)=([^&]*)&*/g, (_, key, value) => {
		const questionKey = key.replace('sf', '')
		params[questionKey] = value
	})

	return params
}
